import React, { useState } from "react";
import { ArrowRight, BookOpen, Download, Instagram } from "lucide-react";
import { BLOG, GET_STARTED, IMG, INSTA } from "../../mock";
import { Icon, Reveal, SectionHeading, Eyebrow } from "../common";
import { useToast } from "../../hooks/use-toast";

export default function Closing() {
  const [email, setEmail] = useState("");
  const { toast } = useToast();

  const getEbook = (e) => {
    e.preventDefault();
    if (!email.includes("@")) {
      toast({ title: "Almost there", description: "Pop in a valid email and we'll send the eBook." });
      return;
    }
    toast({
      title: "eBook on its way!",
      description: `"5 Mistakes Overseas Volunteers Make" is heading to ${email}.`,
    });
    setEmail("");
  };

  return (
    <>
      {/* Blog */}
      <section className="bg-white py-24 lg:py-32">
        <div className="mx-auto max-w-[1400px] px-5 lg:px-8">
          <SectionHeading
            eyebrow="Latest on the blog"
            title="Stories, guides and honest advice"
            text="Everything we've learned from 35 years of sending volunteers around the world."
          />
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {BLOG.map((b, i) => (
              <Reveal key={b.title} delay={i * 90}>
                <article className="group flex h-full flex-col overflow-hidden rounded-3xl bg-white shadow-[0_12px_34px_-24px_rgba(24,40,32,0.45)] ring-1 ring-ivi-mintDeep transition-[transform,box-shadow] duration-300 hover:-translate-y-1.5 hover:shadow-[0_28px_54px_-26px_rgba(24,40,32,0.5)]">
                  <div className="relative h-52 overflow-hidden">
                    <img
                      src={b.img}
                      alt={b.title}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-[900ms] group-hover:scale-110"
                    />
                    <span className="absolute left-4 top-4 rounded-full bg-white/92 px-3 py-1 font-heading text-[11px] font-bold uppercase tracking-[0.14em] text-ivi-greenDark">
                      {b.tag}
                    </span>
                  </div>
                  <div className="flex flex-1 flex-col p-7">
                    <p className="text-[12px] font-semibold uppercase tracking-[0.14em] text-ivi-slateSoft">
                      {b.date}
                    </p>
                    <h3 className="mt-3 font-heading text-[20px] font-bold leading-snug text-ivi-slate transition-colors duration-200 group-hover:text-ivi-greenDark">
                      {b.title}
                    </h3>
                    <p className="mt-3 flex-1 text-[15px] leading-relaxed text-ivi-slateSoft">
                      {b.excerpt}
                    </p>
                    <span className="mt-6 inline-flex items-center gap-2 font-heading text-[14px] font-bold text-ivi-greenDark">
                      Read more
                      <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                    </span>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* eBook */}
      <section className="bg-ivi-sand py-24 lg:py-28">
        <div className="mx-auto max-w-[1400px] px-5 lg:px-8">
          <div className="grid items-center gap-10 rounded-[32px] bg-white p-8 shadow-[0_24px_60px_-40px_rgba(24,40,32,0.5)] ring-1 ring-ivi-mintDeep lg:grid-cols-[1fr_1.1fr_0.8fr] lg:p-12">
            <Reveal className="order-2 lg:order-1">
              <img
                src={IMG.ebook1}
                alt="IVI free eBook mockup"
                className="mx-auto w-full max-w-[300px] animate-float-slow rounded-2xl"
              />
            </Reveal>

            <div className="order-1 lg:order-2">
              <Eyebrow>Free eBook</Eyebrow>
              <h2 className="mt-5 font-heading text-[30px] font-extrabold leading-tight text-ivi-slate sm:text-[38px]">
                5 Mistakes Overseas Volunteers Make
                <span className="block text-ivi-greenDark">and how to avoid them</span>
              </h2>
              <p className="mt-5 text-[16px] leading-relaxed text-ivi-slateSoft">
                Packed with tips and advice: a complete packing guide, how to pick the right
                organisation, getting the best out of your experience, navigating different cultures,
                plus a bonus printable travel journal.
              </p>
              <form onSubmit={getEbook} className="mt-7 flex flex-col gap-3 sm:flex-row">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your email address"
                  className="h-13 flex-1 rounded-full border border-ivi-mintDeep bg-ivi-sand px-5 py-3.5 text-[15px] text-ivi-slate outline-none transition-[border-color,box-shadow] duration-200 focus:border-ivi-green focus:ring-2 focus:ring-ivi-green/25"
                />
                <button
                  type="submit"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-ivi-green px-7 py-3.5 font-heading text-[15px] font-bold text-white transition-[background-color,transform] duration-200 hover:-translate-y-0.5 hover:bg-ivi-greenDark"
                >
                  <Download className="h-4 w-4" /> Get my eBook
                </button>
              </form>
              <p className="mt-3 flex items-center gap-2 text-[13px] text-ivi-slateSoft">
                <BookOpen className="h-3.5 w-3.5" /> 28 pages · No spam, unsubscribe anytime
              </p>
            </div>

            <Reveal className="order-3" delay={120}>
              <img
                src={IMG.ebook2}
                alt="IVI travel journal mockup"
                className="mx-auto w-full max-w-[220px] rounded-2xl"
              />
            </Reveal>
          </div>
        </div>
      </section>

      {/* Instagram marquee */}
      <section className="overflow-hidden bg-white py-20">
        <div className="mx-auto max-w-[1400px] px-5 text-center lg:px-8">
          <Eyebrow>@volunteeroverseas</Eyebrow>
          <h2 className="mt-5 font-heading text-[30px] font-extrabold text-ivi-slate sm:text-[38px]">
            Latest from Instagram
          </h2>
        </div>
        <div className="mt-12 flex w-max animate-marquee-x gap-4 px-4">
          {[...INSTA, ...INSTA].map((src, i) => (
            <a
              key={i}
              href="#insta"
              className="group relative h-52 w-52 shrink-0 overflow-hidden rounded-2xl"
            >
              <img
                src={src}
                alt="IVI volunteers on Instagram"
                loading="lazy"
                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              <span className="absolute inset-0 grid place-items-center bg-ivi-slate/0 text-white opacity-0 transition-[background-color,opacity] duration-300 group-hover:bg-ivi-slate/45 group-hover:opacity-100">
                <Instagram className="h-7 w-7" />
              </span>
            </a>
          ))}
        </div>
      </section>

      {/* Get started */}
      <section id="apply" className="bg-ivi-mint py-24 lg:py-28">
        <div className="mx-auto max-w-[1400px] px-5 lg:px-8">
          <SectionHeading
            eyebrow="Get started"
            title="Ready when you are"
            text="Three easy ways to take the next step no pressure, no obligation."
          />
          <div className="mt-14 grid gap-5 md:grid-cols-3">
            {GET_STARTED.map((g, i) => (
              <Reveal key={g.title} delay={i * 90}>
                <a
                  href="#apply"
                  className="group flex h-full flex-col items-start rounded-3xl bg-white p-9 shadow-[0_12px_34px_-24px_rgba(24,40,32,0.45)] transition-[transform,box-shadow] duration-300 hover:-translate-y-1.5 hover:shadow-[0_30px_56px_-28px_rgba(24,40,32,0.5)]"
                >
                  <span className="grid h-14 w-14 place-items-center rounded-2xl bg-ivi-mint text-ivi-greenDark transition-colors duration-300 group-hover:bg-ivi-green group-hover:text-white">
                    <Icon name={g.icon} className="h-6 w-6" />
                  </span>
                  <h3 className="mt-6 font-heading text-[21px] font-extrabold text-ivi-slate">
                    {g.title}
                  </h3>
                  <p className="mt-3 flex-1 text-[15px] leading-relaxed text-ivi-slateSoft">{g.text}</p>
                  <span className="mt-6 inline-flex items-center gap-2 font-heading text-[14px] font-bold text-ivi-greenDark">
                    {g.cta}
                    <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                  </span>
                </a>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
